// Modules
import { useState, useEffect } from "react"
import { useFormContext } from "react-hook-form"

// Constants
import { ERROR_MESSAGES } from "~constants/errorMessages"

// Utils
import { Validation } from "~utils/regexValidations"
import { countryRegionData } from "~utils/countryRegionData"
import { StockExchangesList } from "~utils/stockExchanges"
import { createDropdownCollection } from "~utils/dropdown"

// Components
import Input from "~app/Input"
import ControlSelect from "~app/ControlSelect"
import InputColorHexWrapper from "~components/InputColorHexWrapper"
import { WizardStepContainer, WizardRow, WizardColumn } from "~styled/wizard"

interface StepProps {
  visible?: boolean
}

// Get country region data
const { countries, regions } = countryRegionData
const countriesList = createDropdownCollection(countries, "name", "code")

// Get available stock exchanges
const stockExchanges = createDropdownCollection(
  StockExchangesList,
  "shortName",
  "shortName"
)

const CompanyModalStep1 = ({ visible = true }: StepProps) => {
  const {
    watch,
    formState: { errors },
    control,
    register,
    setValue,
    getValues,
  } = useFormContext()

  const [countryRegions, setCountryRegions] = useState<any>([])

  useEffect(() => {
    const region = getValues("region")
    const country = getValues("country")

    // Get regions for the selected country
    const regionsData = country ? regions[country.value] : []

    // Clear the selected region if it's not in the new regionsData
    if (region && !regionsData.find(el => el.code === region.value)) {
      setValue("region", null)
    }

    // Update the regions
    setCountryRegions(createDropdownCollection(regionsData, "name", "code"))
  }, [watch("country")])

  return (
    <WizardStepContainer className={visible ? "visible" : ""}>
      <WizardRow>
        <WizardColumn>
          <Input
            name="name"
            label="*Name"
            register={register("name", { required: true })}
            className="name-field"
            errorMessage={errors.name && ERROR_MESSAGES.FIELD_REQUIRED}
          />
        </WizardColumn>

        <WizardColumn>
          <Input
            name="code"
            label="*Code"
            placeholder="e.g. vintage-wine-estates"
            register={register("code", { required: true })}
            className="code-field"
            errorMessage={errors.symbol && ERROR_MESSAGES.FIELD_REQUIRED}
          />
        </WizardColumn>
      </WizardRow>
      <WizardRow>
        <WizardColumn>
          <ControlSelect
            name="exchange"
            label="*Exchange"
            rules={{ required: true }}
            height={200}
            control={control}
            options={stockExchanges}
            className="exchange-field"
            searchable={false}
            errorMessage={errors.exchange && ERROR_MESSAGES.FIELD_REQUIRED}
          />
        </WizardColumn>

        <WizardColumn>
          <Input
            name="symbol"
            label="*Symbol"
            register={register("symbol", { required: true })}
            className="symbol-field"
            errorMessage={errors.symbol && ERROR_MESSAGES.FIELD_REQUIRED}
          />
        </WizardColumn>
      </WizardRow>

      <WizardRow>
        <WizardColumn>
          <Input
            name="description"
            label="*Description"
            register={register("description", { required: true })}
            errorMessage={errors.description && ERROR_MESSAGES.FIELD_REQUIRED}
          />
        </WizardColumn>

        <WizardColumn>
          <InputColorHexWrapper color={watch("color")}>
            <Input
              name="color"
              label="*Color (Hex)"
              register={register("color", {
                required: true,
                pattern: {
                  value: Validation.hexCode,
                  message: ERROR_MESSAGES.INVALID_HEX_CODE,
                },
              })}
              errorMessage={
                errors.color &&
                (errors.color.message || ERROR_MESSAGES.FIELD_REQUIRED)
              }
            />
          </InputColorHexWrapper>
        </WizardColumn>
      </WizardRow>

      <WizardRow>
        <WizardColumn>
          <ControlSelect
            name="country"
            label="*Country"
            rules={{ required: true }}
            height={200}
            control={control}
            options={countriesList}
            position="top"
            errorMessage={errors.country && ERROR_MESSAGES.FIELD_REQUIRED}
          />
        </WizardColumn>

        <WizardColumn>
          <ControlSelect
            name="region"
            label="*State/Region"
            rules={{ required: true }}
            height={200}
            control={control}
            options={countryRegions}
            position="top"
            errorMessage={errors.region && ERROR_MESSAGES.FIELD_REQUIRED}
          />
        </WizardColumn>
      </WizardRow>

      <WizardRow>
        <WizardColumn>
          <Input
            name="city"
            label="*City"
            register={register("city", { required: true })}
            errorMessage={errors.city && ERROR_MESSAGES.FIELD_REQUIRED}
          />
        </WizardColumn>

        <WizardColumn>
          <Input
            name="zipCode"
            label="*Zip Code"
            register={register("zipCode", { required: true })}
            errorMessage={errors.zipCode && ERROR_MESSAGES.FIELD_REQUIRED}
          />
        </WizardColumn>
      </WizardRow>
    </WizardStepContainer>
  )
}

export default CompanyModalStep1
